import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Card, Button, Radio, Checkbox, Input, InputNumber, Select, DatePicker,
         Rate, Spin, Progress, Tag, message, Alert } from 'antd';
import { SendOutlined, SaveOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import SurveyService from '../../services/SurveyService';
import util from '../../utils/util';

const { TextArea } = Input;

export default function QoCSurveyForm({ surveyId, onComplete }) {
    const [survey,   setSurvey]   = useState(null);
    const [answers,  setAnswers]  = useState({});
    const [section,  setSection]  = useState(0);
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        loadSurvey();
    }, [surveyId]); // eslint-disable-line

    const loadSurvey = async () => {
        setLoading(true);
        try {
            const res = await SurveyService.getSurveyDetail(surveyId);
            const data = res.data?.data;
            setSurvey(data);
            // Restore draft from localStorage
            const draft = localStorage.getItem(`survey_draft_${surveyId}`);
            if (draft) setAnswers(JSON.parse(draft));
        } catch(e) { message.error(e.message); }
        finally { setLoading(false); }
    };

    const setAnswer = (qid, val) => {
        const newAnswers = { ...answers, [qid]: val };
        setAnswers(newAnswers);
        localStorage.setItem(`survey_draft_${surveyId}`, JSON.stringify(newAnswers));
    };

    const saveDraft = async () => {
        setSaving(true);
        try {
            await SurveyService.saveResponse(surveyId, { answers });
            message.success('Draft saved');
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    const submit = async () => {
        // Validate required questions in current view
        const requiredUnanswered = currentQuestions.filter(q =>
            q.required && shouldShow(q) && (answers[q.id] === undefined || answers[q.id] === '' || answers[q.id] === null)
        );
        if (requiredUnanswered.length > 0) {
            message.warning(`Please answer ${requiredUnanswered.length} required question(s)`);
            return;
        }
        setSaving(true);
        try {
            await SurveyService.saveResponse(surveyId, { answers, submit: true });
            localStorage.removeItem(`survey_draft_${surveyId}`);
            setSubmitted(true);
            if (onComplete) onComplete();
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    if (loading) return <div style={{textAlign:'center',padding:60}}><Spin size="large"/></div>;
    if (!survey) return <div style={{padding:40,textAlign:'center',color:'#888'}}>Survey not found</div>;

    const sections  = survey.sections?.length ? survey.sections : [{ id:null, title:'Questions' }];
    const currentSec = sections[section];
    const currentQuestions = survey.questions?.filter(q =>
        currentSec.id ? String(q.section_id) === String(currentSec.id) : true
    ) || [];






    if (submitted) return (
        <div style={{textAlign:'center',padding:60}}>
            <div style={{fontSize:48,marginBottom:16}}>✅</div>
            <h2 style={{color:'#16a34a'}}>Survey Submitted!</h2>
            <p style={{color:'#888'}}>Thank you for your response. Your submission has been recorded.</p>
        </div>
    );

    const renderQuestion = (q) => {
        const val = answers[q.id];
        switch(q.type) {
            case 'text':
                return <Input value={val||''} onChange={e=>setAnswer(q.id,e.target.value)}
                    placeholder="Type your answer..." />;
            case 'textarea':
                return <TextArea rows={3} value={val||''} onChange={e=>setAnswer(q.id,e.target.value)}
                    placeholder="Type your answer..." />;
            case 'number':
                return <InputNumber value={val} onChange={v=>setAnswer(q.id,v)}
                    style={{width:'100%'}} placeholder="Enter a number" />;
            case 'single':
                return (
                    <Radio.Group value={val} onChange={e=>setAnswer(q.id,e.target.value)}
                        style={{display:'flex',flexDirection:'column',gap:8}}>
                        {(q.options||[]).map(opt=>(
                            <Radio key={opt} value={opt} style={{padding:'8px 12px',border:'1px solid #e5e7eb',borderRadius:6,
                                background:val===opt?'#eff6ff':'transparent'}}>
                                {opt}
                            </Radio>
                        ))}
                    </Radio.Group>
                );
            case 'multiple':
                return (
                    <Checkbox.Group value={val||[]} onChange={v=>setAnswer(q.id,v)}
                        style={{display:'flex',flexDirection:'column',gap:8}}>
                        {(q.options||[]).map(opt=>(
                            <Checkbox key={opt} value={opt} style={{padding:'8px 12px',border:'1px solid #e5e7eb',borderRadius:6,
                                background:(val||[]).includes(opt)?'#eff6ff':'transparent'}}>
                                {opt}
                            </Checkbox>
                        ))}
                    </Checkbox.Group>
                );
            case 'scale':
                return (
                    <div>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,fontSize:11,color:'#888'}}>
                            <span>{q.scale_min}</span><span>{q.scale_max}</span>
                        </div>
                        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                            {Array.from({length:(q.scale_max-q.scale_min)+1},(_,i)=>i+q.scale_min).map(n=>(
                                <button key={n} onClick={()=>setAnswer(q.id,n)}
                                    style={{width:48,height:48,borderRadius:8,border:'2px solid '+(val===n?'#1890ff':'#e5e7eb'),
                                        background:val===n?'#1890ff':'#fff',color:val===n?'#fff':'#555',
                                        fontWeight:700,fontSize:16,cursor:'pointer',transition:'all 0.15s'}}>
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 'boolean':
                return (
                    <div style={{display:'flex',gap:12}}>
                        {['Yes','No'].map(opt=>(
                            <button key={opt} onClick={()=>setAnswer(q.id,opt)}
                                style={{flex:1,padding:'12px 0',borderRadius:8,border:'2px solid '+(val===opt?'#1890ff':'#e5e7eb'),
                                    background:val===opt?'#1890ff':'#fff',color:val===opt?'#fff':'#555',
                                    fontWeight:700,fontSize:16,cursor:'pointer',transition:'all 0.15s'}}>
                                {opt==='Yes'?'✅ Yes':'❌ No'}
                            </button>
                        ))}
                    </div>
                );
            case 'date':
                return <DatePicker value={val ? (typeof val === 'string' ? moment(val) : val) : null} onChange={v=>setAnswer(q.id,v)} style={{width:'100%'}} />;
            default:
                return <Input value={val||''} onChange={e=>setAnswer(q.id,e.target.value)} />;
        }
    };

    // Evaluate skip logic — returns true if question should be shown
    const shouldShow = (q) => {
        if (!q.skip_logic?.field) return true;
        const { field, operator, value } = q.skip_logic;
        const allQ = survey?.questions || [];
        const refQ = allQ.find(qq => (qq.xlsform_name && qq.xlsform_name === field) || String(qq.id) === String(field));
        if (!refQ) return true;
        const refAnswer = String(answers[refQ.id] || '').toLowerCase();
        const compareVal = String(value || '').toLowerCase();
        if (operator === '=')        return refAnswer === compareVal;
        if (operator === '!=')       return refAnswer !== compareVal;
        if (operator === 'contains') return refAnswer.includes(compareVal);
        return true;
    };
    const visibleAllQ = (survey.questions || []).filter(q => shouldShow(q));
    const totalQ    = visibleAllQ.length || 0;
    const answeredQ = visibleAllQ.filter(q => answers[q.id] !== undefined && answers[q.id] !== '' && answers[q.id] !== null).length;
    const progress  = totalQ ? Math.round(answeredQ / totalQ * 100) : 0;
    const isLast    = section >= sections.length - 1;

    return (
        <div style={{maxWidth:700,margin:'0 auto',padding:'20px 16px'}}>
            {/* Header */}
            <div style={{marginBottom:20}}>
                <h2 style={{margin:0,color:'#0d2137'}}>{survey.title}</h2>
                {survey.description && <p style={{color:'#888',marginTop:4}}>{survey.description}</p>}
                <div style={{display:'flex',alignItems:'center',gap:12,marginTop:12}}>
                    <Progress percent={progress} size="small" style={{flex:1}} />
                    <span style={{fontSize:12,color:'#888',whiteSpace:'nowrap'}}>{answeredQ}/{totalQ} answered</span>
                </div>
            </div>

            {/* Section tabs if multiple */}
            {sections.length > 1 && (
                <div style={{display:'flex',gap:4,marginBottom:16,overflowX:'auto'}}>
                    {sections.map((sec,i)=>(
                        <button key={sec.id||i} onClick={()=>setSection(i)}
                            style={{padding:'6px 14px',borderRadius:6,border:'1px solid '+(i===section?'#1890ff':'#e5e7eb'),
                                background:i===section?'#1890ff':'#fff',color:i===section?'#fff':'#555',
                                cursor:'pointer',whiteSpace:'nowrap',fontSize:12,fontWeight:600}}>
                            {sec.title}
                        </button>
                    ))}
                </div>
            )}

            {/* Questions */}
            {currentQuestions.filter(q => shouldShow(q)).map((q,i)=>(
                <Card key={q.id} size="small" style={{marginBottom:12,
                    borderLeft:`3px solid ${answers[q.id]!==undefined?'#16a34a':'#e5e7eb'}`}}>
                    <div style={{marginBottom:10}}>
                        <span style={{fontWeight:600,fontSize:14}}>{q.label}</span>
                        {q.required && <Tag color="red" style={{marginLeft:8,fontSize:10}}>Required</Tag>}
                        {q.helper_text && <div style={{fontSize:11,color:'#888',marginTop:4}}>{q.helper_text}</div>}
                    </div>
                    {renderQuestion(q)}
                </Card>
            ))}

            {/* Navigation */}
            <div style={{display:'flex',justifyContent:'space-between',marginTop:20,gap:8}}>
                <Button icon={<SaveOutlined/>} loading={saving} onClick={saveDraft}>Save Draft</Button>
                <div style={{display:'flex',gap:8}}>
                    {section > 0 && (
                        <Button icon={<ArrowLeftOutlined/>} onClick={()=>setSection(s=>s-1)}>Back</Button>
                    )}
                    {!isLast ? (
                        <Button type="primary" icon={<ArrowRightOutlined/>} onClick={()=>setSection(s=>s+1)}>
                            Next Section
                        </Button>
                    ) : (
                        <Button type="primary" icon={<SendOutlined/>} loading={saving} onClick={submit}
                            style={{background:'#16a34a',borderColor:'#16a34a'}}>
                            Submit Survey
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
